import React, { useState } from 'react';
import { authApi } from '../services/api';
import { AuthShell } from './AuthShell';
import { Button, TextButton, Field, Input } from './ui';

interface ForgotPasswordProps {
  onBack: () => void;
}

export const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState<'request' | 'reset'>('request');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      await authApi.requestPasswordReset(email);
      setSuccess('A reset token has been generated. Contact the administrator to retrieve it, then enter it below.');
      setStep('reset');
    } catch (err: any) {
      setError(err.message || 'Failed to request password reset');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);

    try {
      await authApi.resetPassword(email, resetToken, newPassword);
      setSuccess('Password reset successfully! Redirecting to login...');
      setTimeout(() => {
        onBack();
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthShell
      title="Reset your password"
      subhead={
        step === 'request'
          ? 'Request a token, then use it to set a new password.'
          : 'Enter the token you were given, then choose a new password.'
      }
      error={error}
      notice={success}
    >
      {step === 'request' ? (
        <form onSubmit={handleRequestReset} className="space-y-6">
          <Field
            label="Email"
            hint="Enter the address on your account."
            htmlFor="fp-email"
          >
            <Input
              id="fp-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              disabled={isLoading}
            />
          </Field>

          <div className="flex items-center justify-between gap-4 pt-1">
            <TextButton type="button" onClick={onBack}>
              Back to sign in
            </TextButton>
            <Button type="submit" variant="primary" disabled={isLoading}>
              {isLoading ? 'Requesting…' : 'Request reset token'}
            </Button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleResetPassword} className="space-y-6">
          <Field
            label="Reset token"
            hint="The token generated for your account."
            htmlFor="fp-token"
          >
            <Input
              id="fp-token"
              type="text"
              required
              value={resetToken}
              onChange={(e) => setResetToken(e.target.value)}
              placeholder="Enter reset token"
              disabled={isLoading}
              className="tracking-[0.02em]"
            />
          </Field>

          <Field label="New password" hint="At least 6 characters." htmlFor="fp-new">
            <Input
              id="fp-new"
              type="password"
              required
              minLength={6}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              disabled={isLoading}
            />
          </Field>

          <Field label="Confirm password" htmlFor="fp-confirm">
            <Input
              id="fp-confirm"
              type="password"
              required
              minLength={6}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              disabled={isLoading}
            />
          </Field>

          <div className="flex items-center justify-between gap-4 pt-1">
            <TextButton type="button" onClick={onBack}>
              Back to sign in
            </TextButton>
            <Button type="submit" variant="primary" disabled={isLoading}>
              {isLoading ? 'Resetting…' : 'Reset password'}
            </Button>
          </div>
        </form>
      )}
    </AuthShell>
  );
};
